import React, { useState } from "react";
import {
  Box, Typography, Button, Card, CardContent,
  Divider, Dialog, DialogContent, DialogActions,
  Switch, FormControlLabel, Alert, Snackbar, List, ListItem,
  ListItemText, ListItemSecondaryAction, IconButton, Chip,
  Stack, alpha, useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  Edit as EditIcon,
  Description as DescriptionIcon,
  ViewModule as SectionIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { aboutService } from "../../../api/services/aboutService";
import AboutSectionBuilder from "./AboutSectionBuilder";
import AboutFieldDialog from "./AboutFieldDialog";
import AboutFieldEditDialog from "./AboutFieldEditDialog";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import CustomInput from "../../FormFields/CustomInput";
import CustomTextArea from "../../CustomForm/CustomTextArea";


const AboutFormBuilder = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [selectedFormId, setSelectedFormId] = useState(null);

  const [formDialog, setFormDialog] = useState(false);
  const [sectionDialog, setSectionDialog] = useState(false);
  const [fieldDialog, setFieldDialog] = useState(false);
  const [fieldEditDialog, setFieldEditDialog] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editingField, setEditingField] = useState(null);

  const [confirmModal, setConfirmModal] = useState({
    show: false, type: '', id: null, title: '', body: '', isDelete: true
  });

  const [formData, setFormData] = useState({
    title: "", description: "", is_active: false
  });

  const [sectionData, setSectionData] = useState({
    title: "", description: "", order: 0
  });

  const [fieldData, setFieldData] = useState({
    label: "", field_type: "singleline", required: false,
    placeholder: "", help_text: "", choices: "", order: 0
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { data: aboutFormsResponse, isLoading: formsLoading, refetch: refetchForms } = useQuery(
    'about-forms',
    aboutService.getAboutForms,
    { onError: () => showSnackbar('Failed to fetch about forms', 'error') }
  );

  const aboutForms = aboutFormsResponse?.results || [];

  const { data: formStructure, isLoading: structureLoading, refetch: refetchStructure } = useQuery(
    ['about-form-structure', selectedFormId],
    () => aboutService.getAboutFormFullStructure(selectedFormId),
    {
      enabled: !!selectedFormId,
      onError: () => showSnackbar('Failed to fetch form structure', 'error')
    }
  );

  const createFormMutation = useMutation(aboutService.createAboutForm, {
    onSuccess: () => {
      queryClient.invalidateQueries('about-forms');
      setFormDialog(false);
      setFormData({ title: '', description: '', is_active: false });
      enqueueSnackbar('About form created successfully', { variant: 'success' });
      refetchForms();
    },
    onError: () => enqueueSnackbar('Failed to create about form', { variant: 'error' })
  });

  const updateFormMutation = useMutation(
    ({ id, data }) => aboutService.updateAboutForm(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('about-forms');
        queryClient.invalidateQueries(['about-form-structure', selectedFormId]);
        enqueueSnackbar('About form updated successfully', { variant: 'success' });
        refetchForms();
        refetchStructure();
      },
      onError: () => enqueueSnackbar('Failed to update about form', { variant: 'error' })
    }
  );

  const deleteFormMutation = useMutation(aboutService.deleteAboutForm, {
    onSuccess: () => {
      queryClient.invalidateQueries('about-forms');
      if (selectedFormId) {
        setSelectedFormId(null);
        queryClient.invalidateQueries(['about-form-structure', selectedFormId]);
      }
      enqueueSnackbar('About form deleted successfully', { variant: 'success' });
      setConfirmModal({ show: false, type: '', id: null, title: '', body: '', isDelete: true });
      refetchForms();
    },
    onError: () => enqueueSnackbar('Failed to delete about form', { variant: 'error' })
  });

  const createSectionMutation = useMutation(aboutService.createAboutSection, {
    onSuccess: () => {
      queryClient.invalidateQueries(['about-form-structure', selectedFormId]);
      setSectionDialog(false);
      setSectionData({ title: '', description: '', order: 0 });
      enqueueSnackbar('About section created successfully', { variant: 'success' });
      refetchStructure();
    },
    onError: () => enqueueSnackbar('Failed to create about section', { variant: 'error' })
  });

  const deleteSectionMutation = useMutation(aboutService.deleteAboutSection, {
    onSuccess: () => {
      queryClient.invalidateQueries(['about-form-structure', selectedFormId]);
      enqueueSnackbar('About section deleted successfully', { variant: 'success' });
      refetchStructure();
      setConfirmModal({ show: false, type: '', id: null, title: '', body: '', isDelete: true });
    },
    onError: () => enqueueSnackbar('Failed to delete about section', { variant: 'error' })
  });

  const createFieldMutation = useMutation(aboutService.createAboutField, {
    onSuccess: () => {
      queryClient.invalidateQueries(['about-form-structure', selectedFormId]);
      setFieldDialog(false);
      setFieldData({
        label: '', field_type: 'singleline', required: false,
        placeholder: '', help_text: '', choices: '', order: 0
      });
      enqueueSnackbar('About field created successfully', { variant: 'success' });
      refetchStructure();
    },
    onError: () => enqueueSnackbar('Failed to create about field', { variant: 'error' })
  });

  const updateFieldMutation = useMutation(
    ({ id, data }) => aboutService.updateAboutField(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['about-form-structure', selectedFormId]);
        setFieldEditDialog(false);
        setEditingField(null);
        enqueueSnackbar('About field updated successfully', { variant: 'success' });
        refetchStructure();
      },
      onError: () => enqueueSnackbar('Failed to update about field', { variant: 'error' })
    }
  );

  const deleteFieldMutation = useMutation(aboutService.deleteAboutField, {
    onSuccess: () => {
      queryClient.invalidateQueries(['about-form-structure', selectedFormId]);
      enqueueSnackbar('About field deleted successfully', { variant: 'success' });
      refetchStructure();
      setConfirmModal({ show: false, type: '', id: null, title: '', body: '', isDelete: true });
    },
    onError: () => enqueueSnackbar('Failed to delete about field', { variant: 'error' })
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '', severity: 'success' });
  };

  const handleFormDelete = (id, title) => {
    setConfirmModal({
      show: true, type: 'deleteForm', id,
      title: 'Delete About Form',
      body: `Are you sure you want to delete "${title}"? This will also delete all sections and fields associated with this form.`,
      isDelete: true
    });
  };

  const handleSectionDelete = (id, title) => {
    setConfirmModal({
      show: true, type: 'deleteSection', id,
      title: 'Delete Section',
      body: `Are you sure you want to delete "${title}"? This will also delete all fields in this section.`,
      isDelete: true
    });
  };

  const handleFieldDelete = (id, label) => {
    setConfirmModal({
      show: true, type: 'deleteField', id,
      title: 'Delete Field',
      body: `Are you sure you want to delete "${label}"?`,
      isDelete: true
    });
  };

  const handleConfirmAction = () => {
    switch (confirmModal.type) {
      case 'deleteForm': deleteFormMutation.mutate(confirmModal.id); break;
      case 'deleteSection': deleteSectionMutation.mutate(confirmModal.id); break;
      case 'deleteField': deleteFieldMutation.mutate(confirmModal.id); break;
      default:
        setConfirmModal({ show: false, type: '', id: null, title: '', body: '', isDelete: true });
    }
  };

  const handleSectionCreate = () => {
    const payload = {
      title: sectionData.title,
      description: sectionData.description,
      form: parseInt(selectedFormId),
      order: sections.length
    };
    createSectionMutation.mutate(payload);
  };

  const handleFieldCreate = (data) => {
    const payload = {
      label: data.label,
      field_type: data.field_type,
      required: data.required,
      placeholder: data.placeholder,
      help_text: data.help_text,
      choices: data.choices,
      section: parseInt(editingSection?.id),
      order: editingSection?.fields?.length || 0
    };
    createFieldMutation.mutate(payload);
  };

  const handleFieldUpdate = (data) => {
    const payload = {
      label: data.label,
      field_type: data.field_type,
      required: data.required,
      placeholder: data.placeholder,
      help_text: data.help_text,
      choices: data.choices,
    };
    updateFieldMutation.mutate({ id: editingField?.id, data: payload });
  };

  const handleToggleActive = (form) => {
    updateFormMutation.mutate({
      id: form.id,
      data: { ...form, is_active: !form.is_active }
    });
  };

  const handleFormEdit = (form) => {
    setFormData({
      title: form.title,
      description: form.description,
      is_active: form.is_active
    });
    setFormDialog(true);
  };

  const sections = formStructure?.sections || [];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto", position: 'relative' }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6" fontWeight={600} sx={{ color: '#0f172a' }}>
            About Form Builder
          </Typography>
          <Typography variant="caption" color="#64748b">
            Create dynamic about forms for client information
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setFormData({ title: '', description: '', is_active: false });
            setFormDialog(true);
          }}
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 20px rgba(79, 70, 229, 0.4)'
            }
          }}
        >
          New About Form
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* LEFT SIDEBAR */}
        <Box sx={{ flex: '0 0 320px', minWidth: '280px' }}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', borderRadius: '12px' }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#0f172a', mb: 1 }}>
                Available Forms
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              {aboutForms.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary" sx={{ fontSize: '0.85rem' }}>
                    No forms available
                  </Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {aboutForms.map((form) => (
                    <ListItem
                      key={form.id}
                      button
                      selected={selectedFormId === form.id}
                      onClick={() => setSelectedFormId(form.id)}
                      sx={{
                        borderRadius: '8px',
                        mb: 0.5,
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        '&.Mui-selected': {
                          bgcolor: 'rgba(99, 102, 241, 0.08)',
                          '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.12)' }
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                        <ListItemText
                          primary={
                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#0f172a' }}>
                              {form.title}
                            </Typography>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Chip
                                label={form.is_active ? 'Active' : 'Inactive'}
                                size="small"
                                sx={{
                                  height: 20, fontSize: '0.6rem',
                                  bgcolor: form.is_active ? '#dcfce7' : '#f1f5f9',
                                  color: form.is_active ? '#16a34a' : '#94a3b8',
                                  '& .MuiChip-label': { px: 1 }
                                }}
                              />
                              <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>
                                {new Date(form.created_at).toLocaleDateString()}
                              </Typography>
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleFormEdit(form); }}
                            sx={{ mr: 0.5 }}
                          >
                            <EditIcon fontSize="small" sx={{ fontSize: '16px' }} />
                          </IconButton>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleFormDelete(form.id, form.title); }}
                            sx={{ border: '1px solid #fee2e2', borderRadius: 1, padding: '2px' }}
                          >
                            <DeleteIcon fontSize="small" sx={{ color: '#ef4444', fontSize: '16px' }} />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* RIGHT CONTENT */}
        <Box sx={{ flex: 1, minWidth: '300px' }}>
          {selectedFormId ? (
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', borderRadius: '12px' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h6" fontWeight={600} sx={{ color: '#0f172a', fontSize: '1.1rem' }}>
                      {formStructure?.title || 'Loading...'}
                    </Typography>
                    <Chip
                      label={formStructure?.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      sx={{
                        height: 24, fontSize: '0.7rem',
                        bgcolor: formStructure?.is_active ? '#dcfce7' : '#f1f5f9',
                        color: formStructure?.is_active ? '#16a34a' : '#94a3b8',
                      }}
                    />
                  </Box>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => handleToggleActive(formStructure)}
                      disabled={updateFormMutation.isLoading}
                      sx={{
                        textTransform: 'none', fontSize: '0.75rem',
                        borderColor: formStructure?.is_active ? '#ef4444' : '#22c55e',
                        color: formStructure?.is_active ? '#ef4444' : '#22c55e',
                      }}
                    >
                      {formStructure?.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setSectionDialog(true)}
                      sx={{
                        textTransform: 'none', fontWeight: 500, fontSize: '0.75rem',
                        background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                        boxShadow: '0 2px 8px rgba(79, 70, 229, 0.25)'
                      }}
                    >
                      Add Section
                    </Button>
                  </Box>
                </Box>

                {formStructure?.description && (
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
                    {formStructure.description}
                  </Typography>
                )}

                <Divider />

                {sections.length === 0 ? (
                  <Box textAlign="center" py={6}>
                    <Typography color="textSecondary" sx={{ fontSize: '0.9rem' }}>
                      No sections found. Click "Add Section" to create one.
                    </Typography>
                  </Box>
                ) : (
                  <AboutSectionBuilder
                    sections={sections}
                    onSectionDelete={handleSectionDelete}
                    onAddField={(section) => {
                      setEditingSection(section);
                      setFieldDialog(true);
                    }}
                    onFieldDelete={handleFieldDelete}
                    onFieldEdit={(field) => {
                      setEditingField(field);
                      setFieldData({
                        label: field.label,
                        field_type: field.field_type,
                        required: field.required,
                        placeholder: field.placeholder || '',
                        help_text: field.help_text || '',
                        choices: field.choices || '',
                        order: field.order || 0
                      });
                      setFieldEditDialog(true);
                    }}
                  />
                )}
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', borderRadius: '12px', height: '400px' }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Box textAlign="center" py={8}>
                  <CategoryIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" fontWeight={500}>
                    Select an about form
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Choose a form from the left or create a new one
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>

      {/* FORM DIALOG */}
      <Dialog
        open={formDialog}
        onClose={() => setFormDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 32, height: 32, borderRadius: "8px",
                bgcolor: alpha("#2469F0", 0.08),
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <DescriptionIcon sx={{ color: "#2469F0", fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
                {selectedFormId && formStructure ? 'Edit About Form' : 'Create About Form'}
              </Typography>
              <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
                Configure form details
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={formData.is_active ? 'Active' : 'Draft'}
            size="small"
            sx={{
              height: 20, fontSize: "0.65rem", fontWeight: 600,
              bgcolor: formData.is_active ? "#D1FAE5" : "#F1F5F9",
              color: formData.is_active ? "#039855" : "#64748B",
            }}
          />
        </Box>

        <DialogContent sx={{ pt: 3, px: 2.5 }}>
          <Stack spacing={2}>
            <CustomInput
              label="Title *"
              name="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              size="medium"
              fullWidth
              required
            />

            <CustomTextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />

            <Box
              sx={{
                p: 1.5,
                bgcolor: "#F8FAFC",
                borderRadius: "6px",
                border: "1px solid #E2E8F0",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    size="small"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600} color="#1A1A2E">
                      Active Status
                    </Typography>
                    <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.65rem" }}>
                      Enable this form for client use
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: "1px solid #E2E8F0",
            p: 2,
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setFormDialog(false)}
            startIcon={<CancelIcon sx={{ fontSize: 18 }} />}
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderRadius: "6px",
              borderColor: "#D0D5DD",
              color: "#344054",
              px: 3,
              py: 0.75,
              "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (selectedFormId && formStructure) {
                updateFormMutation.mutate({ id: selectedFormId, data: formData });
              } else {
                createFormMutation.mutate(formData);
              }
            }}
            variant="contained"
            disabled={createFormMutation.isLoading || updateFormMutation.isLoading}
            startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "6px",
              bgcolor: "#2469F0",
              px: 3,
              py: 0.75,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {createFormMutation.isLoading || updateFormMutation.isLoading
              ? "Saving..."
              : selectedFormId && formStructure ? "Update Form" : "Create Form"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SECTION DIALOG */}
      <Dialog
        open={sectionDialog}
        onClose={() => setSectionDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 2.5,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: "#F8FAFC",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Box
            sx={{
              width: 32, height: 32, borderRadius: "8px",
              bgcolor: alpha("#F59E0B", 0.08),
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <SectionIcon sx={{ color: "#F59E0B", fontSize: 18 }} />
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#1A1A2E">
              Create About Section
            </Typography>
            <Typography variant="caption" color="#64748b" sx={{ fontSize: "0.7rem" }}>
              Group fields under a section
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ pt: 3, px: 2.5 }}>
          <Stack spacing={2}>
            <CustomInput
              label="Title *"
              name="title"
              value={sectionData.title}
              onChange={(e) => setSectionData({ ...sectionData, title: e.target.value })}
              size="medium"
              fullWidth
              required
            />

            <CustomTextArea
              label="Description"
              name="description"
              value={sectionData.description}
              onChange={(e) => setSectionData({ ...sectionData, description: e.target.value })}
              rows={3}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #E2E8F0", p: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setSectionDialog(false)}
            startIcon={<CancelIcon sx={{ fontSize: 18 }} />}
            size="small"
            sx={{
              textTransform: "none", fontWeight: 500, borderRadius: "6px",
              borderColor: "#D0D5DD", color: "#344054", px: 3, py: 0.75,
              "&:hover": { bgcolor: "#F9FAFB", borderColor: "#98A2B3" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSectionCreate}
            variant="contained"
            disabled={createSectionMutation.isLoading}
            startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
            size="small"
            sx={{
              textTransform: "none", fontWeight: 600, borderRadius: "6px",
              bgcolor: "#2469F0", px: 3, py: 0.75,
              "&:hover": {
                bgcolor: "#1A5BD4",
                boxShadow: "0px 2px 8px rgba(36, 105, 240, 0.3)",
              },
            }}
          >
            {createSectionMutation.isLoading ? "Creating..." : "Create Section"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* FIELD DIALOGS */}
      <AboutFieldDialog
        open={fieldDialog}
        onClose={() => {
          setFieldDialog(false);
          setFieldData({
            label: '', field_type: 'singleline', required: false,
            placeholder: '', help_text: '', choices: '', order: 0
          });
        }}
        section={editingSection}
        onFieldCreated={handleFieldCreate}
        isLoading={createFieldMutation.isLoading}
        fieldData={fieldData}
        setFieldData={setFieldData}
      />

      <AboutFieldEditDialog
        open={fieldEditDialog}
        onClose={() => {
          setFieldEditDialog(false);
          setEditingField(null);
        }}
        field={editingField}
        onFieldUpdated={handleFieldUpdate}
        isLoading={updateFieldMutation.isLoading}
        fieldData={fieldData}
        setFieldData={setFieldData}
      />

      <ConfirmModal
        show={confirmModal.show}
        handleClose={() => setConfirmModal({ show: false, type: '', id: null, title: '', body: '', isDelete: true })}
        handleConfirm={handleConfirmAction}
        isLoading={deleteFormMutation.isLoading || deleteSectionMutation.isLoading || deleteFieldMutation.isLoading}
        title={confirmModal.title}
        body={confirmModal.body}
        confirmText="Delete"
        cancelText="Cancel"
        isDelete={true}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AboutFormBuilder;